// ---------------------------------------------------------------------------
// SOC-004 — Social Platform Calendar (frontend)
// ---------------------------------------------------------------------------
// Month-grid view of every tenant-scoped scheduled / published / failed
// social post across the seven platform plugins (Facebook, Instagram,
// LinkedIn, TikTok, X/Twitter, YouTube, Pinterest). Audit row:
// docs/audits/social-content-community-reality-2026-05-17.md SOC-004.
//
// Backend contract: `GET /v1/social/calendar/grid?from=ISO&to=ISO&
// platforms=...&statuses=...&viewMode=...` returns
// `{ data: { items[], days[], platforms[], statuses[], viewMode, from, to,
// telemetry } }`. See `workers/api-crm/src/routes/social-calendar.ts`.
//
// CSS uses the Stero design tokens — no hardcoded brand colors. No emojis.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Grid3x3,
  Loader2,
  List,
  CalendarRange,
} from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { EmptyState } from "@/components/ui/empty-state";
import {
  SOCIAL_CALENDAR_PLATFORMS,
  SOCIAL_CALENDAR_STATUSES,
  SOCIAL_CALENDAR_VIEW_MODES,
  SOCIAL_PLATFORM_VISUALS,
  SOCIAL_STATUS_VISUALS,
  buildMonthGrid,
  dateInputToIsoEnd,
  dateInputToIsoStart,
  defaultMonthRange,
  formatItemTime,
  formatMonthLabel,
  monthRange,
  stepMonth,
  type SocialCalendarGridResponse,
  type SocialCalendarItem,
  type SocialCalendarPlatform,
  type SocialCalendarStatus,
  type SocialCalendarViewMode,
} from "./social-calendar-types";

// Re-export so the test file (and future MCP/MCP consumers) imports from
// the route module — matches the CalendarUnified precedent.
export {
  SOCIAL_CALENDAR_PLATFORMS,
  SOCIAL_CALENDAR_STATUSES,
  SOCIAL_CALENDAR_VIEW_MODES,
  type SocialCalendarItem,
  type SocialCalendarPlatform,
  type SocialCalendarStatus,
  type SocialCalendarViewMode,
} from "./social-calendar-types";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface SocialCalendarState {
  status: "idle" | "loading" | "ready" | "error";
  items: SocialCalendarItem[];
  error: string | null;
}

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function SocialCalendarRoute() {
  const initial = useMemo(defaultMonthRange, []);
  const [anchor, setAnchor] = useState<string>(initial.anchor);
  const [viewMode, setViewMode] = useState<SocialCalendarViewMode>("month");
  const [activePlatforms, setActivePlatforms] = useState<
    SocialCalendarPlatform[]
  >([...SOCIAL_CALENDAR_PLATFORMS]);
  const [activeStatuses, setActiveStatuses] = useState<SocialCalendarStatus[]>([
    ...SOCIAL_CALENDAR_STATUSES,
  ]);
  const [state, setState] = useState<SocialCalendarState>({
    status: "idle",
    items: [],
    error: null,
  });

  const range = useMemo(() => monthRange(anchor), [anchor]);

  const loadCalendar = useCallback(async () => {
    setState((prev) => ({ ...prev, status: "loading", error: null }));
    const params = new URLSearchParams();
    params.set("from", dateInputToIsoStart(range.from));
    params.set("to", dateInputToIsoEnd(range.to));
    for (const p of activePlatforms) {
      params.append("platforms", p);
    }
    for (const s of activeStatuses) {
      params.append("statuses", s);
    }
    params.set("viewMode", viewMode);

    try {
      const response = await apiFetch<SocialCalendarGridResponse>(
        `/v1/social/calendar/grid?${params.toString()}`,
      );
      setState({
        status: "ready",
        items: response.data.items,
        error: null,
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to load social calendar";
      setState({ status: "error", items: [], error: message });
    }
  }, [range.from, range.to, activePlatforms, activeStatuses, viewMode]);

  useEffect(() => {
    void loadCalendar();
  }, [loadCalendar]);

  const togglePlatform = useCallback((platform: SocialCalendarPlatform) => {
    setActivePlatforms((prev) => {
      const has = prev.includes(platform);
      if (has) {
        const next = prev.filter((p) => p !== platform);
        return next.length === 0 ? [...SOCIAL_CALENDAR_PLATFORMS] : next;
      }
      return [...prev, platform];
    });
  }, []);

  const toggleStatus = useCallback((status: SocialCalendarStatus) => {
    setActiveStatuses((prev) => {
      const has = prev.includes(status);
      if (has) {
        const next = prev.filter((s) => s !== status);
        return next.length === 0 ? [...SOCIAL_CALENDAR_STATUSES] : next;
      }
      return [...prev, status];
    });
  }, []);

  const goPrev = useCallback(() => setAnchor((a) => stepMonth(a, -1)), []);
  const goNext = useCallback(() => setAnchor((a) => stepMonth(a, +1)), []);
  const goToday = useCallback(
    () => setAnchor(defaultMonthRange().anchor),
    [],
  );

  return (
    <div className="space-y-6" data-testid="social-calendar-root">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-foreground)]">
            Social calendar
          </h1>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Every scheduled post across all your social channels — one view.
          </p>
        </div>
        <ViewModeSwitcher
          viewMode={viewMode}
          onChange={setViewMode}
        />
      </header>

      <MonthNavigator
        anchor={anchor}
        onPrev={goPrev}
        onNext={goNext}
        onToday={goToday}
      />

      <Filters
        activePlatforms={activePlatforms}
        activeStatuses={activeStatuses}
        onTogglePlatform={togglePlatform}
        onToggleStatus={toggleStatus}
      />

      {state.status === "error" ? (
        <ErrorBanner error={state.error ?? ""} onRetry={loadCalendar} />
      ) : null}

      {state.status === "loading" ? (
        <div
          className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]"
          data-testid="social-calendar-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading social posts…
        </div>
      ) : null}

      {state.status === "ready" && state.items.length === 0 ? (
        <EmptyState
          icon={<CalendarDays className="h-8 w-8" aria-hidden="true" />}
          title="No scheduled social posts in this range"
          description="Pick a different month, widen your platform / status filters, or schedule a new post to see it here."
          variant="info"
        />
      ) : null}

      {state.status === "ready" && state.items.length > 0 ? (
        <CalendarBody
          viewMode={viewMode}
          anchor={anchor}
          items={state.items}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// View mode switcher
// ---------------------------------------------------------------------------

const VIEW_MODE_VISUAL: Record<
  SocialCalendarViewMode,
  { label: string; icon: typeof Grid3x3 }
> = {
  month: { label: "Month", icon: Grid3x3 },
  week: { label: "Week", icon: CalendarRange },
  list: { label: "List", icon: List },
};

function ViewModeSwitcher({
  viewMode,
  onChange,
}: {
  viewMode: SocialCalendarViewMode;
  onChange: (v: SocialCalendarViewMode) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Calendar view mode"
      className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-background)] p-1"
    >
      {SOCIAL_CALENDAR_VIEW_MODES.map((mode) => {
        const visual = VIEW_MODE_VISUAL[mode];
        const Icon = visual.icon;
        const isActive = viewMode === mode;
        return (
          <button
            key={mode}
            type="button"
            aria-pressed={isActive}
            data-testid={`social-calendar-view-${mode}`}
            onClick={() => onChange(mode)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition ${
              isActive
                ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground,white)]"
                : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
            }`}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            {visual.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month navigator
// ---------------------------------------------------------------------------

function MonthNavigator({
  anchor,
  onPrev,
  onNext,
  onToday,
}: {
  anchor: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]/60 px-4 py-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onPrev}
          aria-label="Previous month"
          data-testid="social-calendar-prev"
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--color-border)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={onNext}
          aria-label="Next month"
          data-testid="social-calendar-next"
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--color-border)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={onToday}
          data-testid="social-calendar-today"
          className="rounded-md border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
        >
          Today
        </button>
      </div>
      <h2
        className="text-base font-semibold text-[var(--color-foreground)]"
        data-testid="social-calendar-month-label"
      >
        {formatMonthLabel(anchor)}
      </h2>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filters — platform chips + status chips
// ---------------------------------------------------------------------------

function Filters({
  activePlatforms,
  activeStatuses,
  onTogglePlatform,
  onToggleStatus,
}: {
  activePlatforms: SocialCalendarPlatform[];
  activeStatuses: SocialCalendarStatus[];
  onTogglePlatform: (p: SocialCalendarPlatform) => void;
  onToggleStatus: (s: SocialCalendarStatus) => void;
}) {
  return (
    <div className="space-y-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]/60 p-4">
      <div
        className="flex flex-wrap items-center gap-2"
        role="group"
        aria-label="Filter by social platform"
      >
        <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted-foreground)]">
          Platforms
        </span>
        {SOCIAL_CALENDAR_PLATFORMS.map((platform) => {
          const visual = SOCIAL_PLATFORM_VISUALS[platform];
          const Icon = visual.icon;
          const isActive = activePlatforms.includes(platform);
          return (
            <button
              key={platform}
              type="button"
              aria-pressed={isActive}
              data-testid={`social-calendar-filter-platform-${platform}`}
              onClick={() => onTogglePlatform(platform)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition ${
                isActive
                  ? visual.badgeClass
                  : "border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
              }`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {visual.label}
            </button>
          );
        })}
      </div>
      <div
        className="flex flex-wrap items-center gap-2"
        role="group"
        aria-label="Filter by post status"
      >
        <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted-foreground)]">
          Status
        </span>
        {SOCIAL_CALENDAR_STATUSES.map((status) => {
          const visual = SOCIAL_STATUS_VISUALS[status];
          const isActive = activeStatuses.includes(status);
          return (
            <button
              key={status}
              type="button"
              aria-pressed={isActive}
              data-testid={`social-calendar-filter-status-${status}`}
              onClick={() => onToggleStatus(status)}
              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition ${
                isActive
                  ? visual.badgeClass
                  : "border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
              }`}
            >
              {visual.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar body — switches between month grid / week strip / flat list
// ---------------------------------------------------------------------------

function CalendarBody({
  viewMode,
  anchor,
  items,
}: {
  viewMode: SocialCalendarViewMode;
  anchor: string;
  items: SocialCalendarItem[];
}) {
  if (viewMode === "list") {
    return <ListView items={items} />;
  }
  if (viewMode === "week") {
    return <WeekStrip items={items} />;
  }
  return <MonthGrid anchor={anchor} items={items} />;
}

function MonthGrid({
  anchor,
  items,
}: {
  anchor: string;
  items: SocialCalendarItem[];
}) {
  const cells = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const itemsByDay = useMemo(() => {
    const map = new Map<string, SocialCalendarItem[]>();
    for (const item of items) {
      const bucket = map.get(item.dayKey);
      if (bucket) bucket.push(item);
      else map.set(item.dayKey, [item]);
    }
    return map;
  }, [items]);

  return (
    <div
      className="overflow-hidden rounded-2xl border border-[var(--color-border)]"
      data-testid="social-calendar-month-grid"
    >
      <div className="grid grid-cols-7 border-b border-[var(--color-border)] bg-[var(--color-surface)]/50 text-xs font-medium uppercase tracking-wide text-[var(--color-muted-foreground)]">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="px-2 py-2 text-center">
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 divide-x divide-y divide-[var(--color-border)]">
        {cells.map((cell) => {
          const cellItems = itemsByDay.get(cell.dayKey) ?? [];
          return (
            <div
              key={cell.dayKey}
              data-testid={`social-calendar-day-${cell.dayKey}`}
              data-in-month={cell.inMonth ? "true" : "false"}
              className={`min-h-24 p-2 ${
                cell.inMonth
                  ? "bg-[var(--color-background)]"
                  : "bg-[var(--color-muted)]/30 text-[var(--color-muted-foreground)]"
              }`}
            >
              <div className="mb-1 text-xs font-medium text-[var(--color-foreground)]">
                {cell.dayOfMonth}
              </div>
              <ul className="space-y-1" aria-label={`Posts on ${cell.dayKey}`}>
                {cellItems.map((item) => (
                  <PostCard key={item.id} item={item} />
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekStrip({ items }: { items: SocialCalendarItem[] }) {
  // Group by day to mirror the month bucket layer; week-strip view keeps a
  // horizontally-scrollable single row of day columns so dense calendars
  // stay readable on narrow viewports.
  const days = useMemo(() => {
    const map = new Map<string, SocialCalendarItem[]>();
    for (const item of items) {
      const bucket = map.get(item.dayKey);
      if (bucket) bucket.push(item);
      else map.set(item.dayKey, [item]);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  return (
    <div
      className="grid grid-flow-col auto-cols-[minmax(180px,1fr)] gap-3 overflow-x-auto pb-1"
      data-testid="social-calendar-week-strip"
    >
      {days.map(([dayKey, dayItems]) => (
        <div
          key={dayKey}
          className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-3"
        >
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--color-muted-foreground)]">
            {dayKey}
          </div>
          <ul className="space-y-2">
            {dayItems.map((item) => (
              <PostCard key={item.id} item={item} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ListView({ items }: { items: SocialCalendarItem[] }) {
  return (
    <ol
      className="space-y-2"
      data-testid="social-calendar-list"
      aria-label="Scheduled social posts"
    >
      {items.map((item) => {
        const platform = SOCIAL_PLATFORM_VISUALS[item.platform];
        const status = SOCIAL_STATUS_VISUALS[item.status];
        const Icon = platform.icon;
        return (
          <li
            key={item.id}
            data-testid={`social-calendar-list-item-${item.id}`}
            data-platform={item.platform}
            data-status={item.status}
            className="flex items-start gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3"
          >
            <span
              className={`mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full ${platform.dotClass} text-white`}
              aria-hidden="true"
            >
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${platform.badgeClass}`}
                >
                  {platform.label}
                </span>
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${status.badgeClass}`}
                >
                  {status.label}
                </span>
                <time
                  className="text-xs text-[var(--color-muted-foreground)]"
                  dateTime={item.date}
                >
                  {item.dayKey} {formatItemTime(item.date)}
                </time>
              </div>
              <p className="mt-1 truncate text-sm font-medium text-[var(--color-foreground)]">
                {item.title}
              </p>
              {item.accountName ? (
                <p className="mt-0.5 truncate text-xs text-[var(--color-muted-foreground)]">
                  {item.accountName}
                </p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Post card — used inside month grid + week strip cells.
// ---------------------------------------------------------------------------

function PostCard({ item }: { item: SocialCalendarItem }) {
  const platform = SOCIAL_PLATFORM_VISUALS[item.platform];
  const status = SOCIAL_STATUS_VISUALS[item.status];
  const Icon = platform.icon;
  return (
    <li
      data-testid={`social-calendar-post-${item.id}`}
      data-platform={item.platform}
      data-status={item.status}
      className={`flex items-center gap-1.5 truncate rounded-md border px-1.5 py-1 text-[11px] ${platform.badgeClass}`}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="truncate font-medium">{item.title}</span>
      <span
        className={`ml-auto shrink-0 rounded-sm border px-1 py-0 text-[9px] uppercase ${status.badgeClass}`}
      >
        {status.label}
      </span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Error banner — mirrors the CalendarUnified pattern.
// ---------------------------------------------------------------------------

function ErrorBanner({
  error,
  onRetry,
}: {
  error: string;
  onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      data-testid="social-calendar-error"
      className="flex items-start gap-3 rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]"
    >
      <AlertCircle className="mt-0.5 h-4 w-4" aria-hidden="true" />
      <div className="flex-1">
        <p className="font-medium">Couldn&apos;t load social calendar</p>
        <p className="mt-0.5 opacity-90">{error}</p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-md border border-[var(--color-destructive)]/40 px-3 py-1 text-xs font-medium hover:bg-[var(--color-destructive)]/10"
      >
        Retry
      </button>
    </div>
  );
}
